<template>
  <el-dialog
    :model-value="current !== null"
    :fullscreen="isMobile"
    width="90%"
    top="4vh"
    append-to-body
    destroy-on-close
    class="task-attachment-viewer"
    :show-close="false"
    @update:model-value="(open: boolean) => { if (!open) close() }"
  >
    <template #header>
      <div v-if="current" class="flex items-center gap-2 min-w-0">
        <Icon :icon="TASK_ATTACHMENT_ICONS[kind]" class="w-5 h-5 text-gray-500 flex-shrink-0" />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-gray-800 truncate" :title="current.originalFilename">{{ current.originalFilename }}</p>
          <p class="text-xs text-gray-400">
            {{ formatFileSize(current.size) }}
            <template v-if="attachments.length > 1"> · {{ (index ?? 0) + 1 }} / {{ attachments.length }}</template>
          </p>
        </div>
        <button type="button" class="p-2 rounded-md text-gray-500 hover:bg-gray-100" :title="t('openInNewTab')" @click="openInNewTab">
          <Icon icon="mdi:open-in-new" class="w-5 h-5" />
        </button>
        <button type="button" class="p-2 rounded-md text-gray-500 hover:bg-gray-100" :title="t('download')" @click="$emit('download', current)">
          <Icon icon="mdi:download-outline" class="w-5 h-5" />
        </button>
        <button type="button" class="p-2 rounded-md text-gray-500 hover:bg-gray-100" :title="t('close')" @click="close">
          <Icon icon="mdi:close" class="w-5 h-5" />
        </button>
      </div>
    </template>

    <div v-if="current" class="relative bg-gray-50 rounded-lg overflow-hidden viewer-body">
      <!-- Image -->
      <div v-if="kind === 'image' && !loadError" class="w-full h-full flex items-center justify-center overflow-auto" @dblclick="zoomed = !zoomed">
        <img
          :src="url"
          :alt="current.originalFilename"
          class="transition-transform select-none"
          :class="zoomed ? 'max-w-none cursor-zoom-out' : 'max-w-full max-h-full object-contain cursor-zoom-in'"
          @click="zoomed = !zoomed"
          @error="loadError = true"
        />
      </div>

      <!-- PDF -->
      <iframe v-else-if="kind === 'pdf'" :src="url" class="w-full h-full border-0 bg-white" :title="current.originalFilename" />

      <!-- Video -->
      <div v-else-if="kind === 'video' && !loadError" class="w-full h-full flex items-center justify-center bg-black">
        <video :src="url" controls playsinline class="max-w-full max-h-full" @error="loadError = true" />
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="w-full h-full flex items-center justify-center text-gray-400">
        <Icon icon="mdi:loading" class="w-8 h-8 animate-spin" />
      </div>

      <!-- Spreadsheet / CSV -->
      <div v-else-if="sheets.length" class="w-full h-full flex flex-col">
        <div v-if="sheets.length > 1" class="flex gap-1 p-2 border-b border-gray-200 bg-white overflow-x-auto flex-shrink-0">
          <button
            v-for="(sheet, i) in sheets"
            :key="sheet.name"
            type="button"
            class="px-3 py-1 rounded-md text-xs whitespace-nowrap"
            :class="i === activeSheet ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            @click="activeSheet = i"
          >
            {{ sheet.name }}
          </button>
        </div>
        <div class="flex-1 overflow-auto bg-white">
          <table class="sheet-table">
            <tbody>
              <tr v-for="(row, r) in sheets[activeSheet].rows" :key="r">
                <td v-for="(cell, c) in row" :key="c">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="sheets[activeSheet].truncated" class="text-xs text-gray-400 p-2">{{ t('truncated', { n: MAX_ROWS }) }}</p>
        </div>
      </div>

      <!-- Plain text -->
      <pre v-else-if="text !== null" class="w-full h-full overflow-auto p-4 text-sm text-gray-800 whitespace-pre-wrap break-words bg-white" dir="auto">{{ text }}</pre>

      <!-- Office documents via Office Online viewer (public URLs only) -->
      <iframe v-else-if="officeViewerURL" :src="officeViewerURL" class="w-full h-full border-0 bg-white" :title="current.originalFilename" />

      <!-- Fallback -->
      <div v-else class="w-full h-full flex flex-col items-center justify-center gap-3 text-center p-6">
        <Icon :icon="TASK_ATTACHMENT_ICONS[kind]" class="w-16 h-16 text-gray-300" />
        <p class="text-sm text-gray-500">{{ loadError ? t('loadFailed') : t('noPreview') }}</p>
        <el-button type="primary" @click="$emit('download', current)">
          <Icon icon="mdi:download-outline" class="w-4 h-4 me-1" />
          {{ t('download') }}
        </el-button>
      </div>

      <!-- Navigation -->
      <template v-if="attachments.length > 1">
        <button
          type="button"
          class="nav-btn start-2"
          :title="t('previous')"
          @click="go(-1)"
        >
          <Icon icon="mdi:chevron-left" class="w-6 h-6 rtl:rotate-180" />
        </button>
        <button
          type="button"
          class="nav-btn end-2"
          :title="t('next')"
          @click="go(1)"
        >
          <Icon icon="mdi:chevron-right" class="w-6 h-6 rtl:rotate-180" />
        </button>
      </template>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'
import { useMediaQuery } from '@vueuse/core'
import * as XLSX from 'xlsx'
import { TaskAttachment } from '@/models/hr/Task'
import { TASK_ATTACHMENT_ICONS, formatFileSize, getTaskAttachmentKind, isPublicURL } from '@/lib/taskAttachments'

const MAX_ROWS = 1000

const props = defineProps<{
  attachments: TaskAttachment[]
  index: number | null
  urlOf: (attachment: TaskAttachment) => string
}>()

const emit = defineEmits<{
  (e: 'update:index', index: number | null): void
  (e: 'download', attachment: TaskAttachment): void
}>()

const { t } = useI18n({
  messages: {
    en: {
      download: 'Download', close: 'Close', openInNewTab: 'Open in new tab', previous: 'Previous', next: 'Next',
      noPreview: 'Preview is not available for this file type.',
      loadFailed: 'Could not load this file.',
      truncated: 'Showing the first {n} rows.'
    },
    ar: {
      download: 'تنزيل', close: 'إغلاق', openInNewTab: 'فتح في علامة تبويب جديدة', previous: 'السابق', next: 'التالي',
      noPreview: 'المعاينة غير متاحة لهذا النوع من الملفات.',
      loadFailed: 'تعذر تحميل هذا الملف.',
      truncated: 'يتم عرض أول {n} صف.'
    }
  }
})

const isMobile = useMediaQuery('(max-width: 767px)')

const current = computed(() => (props.index === null ? null : props.attachments[props.index] ?? null))
const kind = computed(() => (current.value ? getTaskAttachmentKind(current.value.originalFilename || current.value.filename, current.value.mimetype) : 'file'))
const url = computed(() => (current.value ? props.urlOf(current.value) : ''))
const officeViewerURL = computed(() =>
  (kind.value === 'document' || kind.value === 'presentation') && isPublicURL(url.value)
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url.value)}`
    : ''
)

const loading = ref(false)
const loadError = ref(false)
const zoomed = ref(false)
const text = ref<string | null>(null)
const sheets = ref<{ name: string; rows: string[][]; truncated: boolean }[]>([])
const activeSheet = ref(0)

const toSheet = (name: string, rows: unknown[][]) => ({
  name,
  rows: rows.slice(0, MAX_ROWS).map(row => row.map(cell => (cell === null || cell === undefined ? '' : String(cell)))),
  truncated: rows.length > MAX_ROWS
})

let loadToken = 0
const loadContent = async () => {
  const token = ++loadToken
  loadError.value = false
  zoomed.value = false
  text.value = null
  sheets.value = []
  activeSheet.value = 0
  const attachment = current.value
  if (!attachment) return
  const name = (attachment.originalFilename || attachment.filename).toLowerCase()
  const isCSV = name.endsWith('.csv')
  if (kind.value !== 'text' && kind.value !== 'spreadsheet') return

  loading.value = true
  try {
    const response = await fetch(url.value)
    if (!response.ok) throw new Error(response.statusText)
    if (kind.value === 'text' || isCSV) {
      const content = await response.text()
      if (token !== loadToken) return
      if (isCSV) {
        const workbook = XLSX.read(content, { type: 'string', raw: true })
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        sheets.value = [toSheet(attachment.originalFilename, XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' }))]
      } else {
        text.value = content
      }
    } else {
      const buffer = await response.arrayBuffer()
      if (token !== loadToken) return
      const workbook = XLSX.read(buffer, { type: 'array' })
      sheets.value = workbook.SheetNames.map(sheetName =>
        toSheet(sheetName, XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], { header: 1, defval: '' }))
      )
    }
  } catch (e) {
    console.error(e)
    if (token === loadToken) loadError.value = true
  } finally {
    if (token === loadToken) loading.value = false
  }
}

watch(current, loadContent, { immediate: true })

const close = () => emit('update:index', null)

const go = (step: number) => {
  if (props.index === null || !props.attachments.length) return
  const count = props.attachments.length
  emit('update:index', (props.index + step + count) % count)
}

const openInNewTab = () => {
  if (url.value) window.open(url.value, '_blank', 'noopener')
}

const onKeydown = (e: KeyboardEvent) => {
  if (current.value === null || props.attachments.length < 2) return
  const rtl = document.documentElement.dir === 'rtl'
  if (e.key === 'ArrowRight') go(rtl ? -1 : 1)
  else if (e.key === 'ArrowLeft') go(rtl ? 1 : -1)
}

watch(() => current.value !== null, (open) => {
  if (open) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
}, { immediate: true })

onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.viewer-body {
  height: 75vh;
}

:global(.task-attachment-viewer.is-fullscreen) .viewer-body {
  height: calc(100dvh - 110px);
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: rgba(17, 24, 39, 0.55);
  color: #fff;
}

.nav-btn:hover {
  background: rgba(17, 24, 39, 0.75);
}

.sheet-table {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 100%;
}

.sheet-table td {
  border: 1px solid #e5e7eb;
  padding: 4px 8px;
  white-space: nowrap;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sheet-table tr:first-child td {
  background: #f9fafb;
  font-weight: 600;
  position: sticky;
  top: 0;
}
</style>
